/**
 * 구독 서비스 (Issue #446 PR4)
 *
 * RevenueCat SDK 래핑. 외부 노출 타입은 `@tldr/core` opaque (Like) 타입을 사용해
 * 호출처가 RC SDK에 직접 의존하지 않도록 합니다. 내부 RC SDK 호출 결과는
 * `as unknown as` 캐스팅으로 opaque 타입에 매핑.
 */

import { Capacitor } from '@capacitor/core';
import type { ServiceResult } from './types';
import type {
  CustomerInfoLike,
  PurchasesOfferingsLike,
  PurchasesPackageLike,
} from '@tldr/core';

const REVENUECAT_API_KEY = 'appl_UpJHAxQJOVBacXtaMQsYDvFVxkW';
const ENTITLEMENT_ID = 'pro';

/** 구독 상태 정보 */
export interface SubscriptionInfo {
  isPro: boolean;
  plan: 'free' | 'premium';
  expirationDate: string | null;
  productId: string | null;
}

// Lazy-loaded module cache
let _module: typeof import('@revenuecat/purchases-capacitor') | null = null;

async function loadModule() {
  if (!_module) {
    _module = await import('@revenuecat/purchases-capacitor');
  }
  return _module;
}

interface ActiveEntitlement {
  expirationDate?: string | null;
  productIdentifier?: string;
}

function extractSubscriptionInfo(customerInfo: CustomerInfoLike): SubscriptionInfo {
  const proEntitlement = customerInfo.entitlements?.active?.[ENTITLEMENT_ID] as
    | ActiveEntitlement
    | undefined;
  const isPro = proEntitlement !== undefined;
  return {
    isPro,
    plan: isPro ? 'premium' : 'free',
    expirationDate: proEntitlement?.expirationDate ?? null,
    productId: proEntitlement?.productIdentifier ?? null,
  };
}

/** RevenueCat SDK 초기화 */
export async function configureRevenueCat(userId: string): Promise<ServiceResult<void>> {
  if (!Capacitor.isNativePlatform()) {
    return { success: true, data: undefined };
  }
  try {
    const { Purchases } = await loadModule();
    await Purchases.configure({
      apiKey: REVENUECAT_API_KEY,
      appUserID: userId,
    });
    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'UNKNOWN_ERROR',
        message: error instanceof Error ? error.message : 'RevenueCat 초기화 실패',
      },
    };
  }
}

/** 구독 상태 조회 */
export async function getSubscriptionStatus(): Promise<ServiceResult<SubscriptionInfo>> {
  if (!Capacitor.isNativePlatform()) {
    return {
      success: true,
      data: { isPro: false, plan: 'free', expirationDate: null, productId: null },
    };
  }
  try {
    const { Purchases } = await loadModule();
    const { customerInfo } = await Purchases.getCustomerInfo();
    return {
      success: true,
      data: extractSubscriptionInfo(customerInfo as unknown as CustomerInfoLike),
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'UNKNOWN_ERROR',
        message: error instanceof Error ? error.message : '구독 상태 조회 실패',
      },
    };
  }
}

/** Offerings 조회 */
export async function getOfferings(): Promise<ServiceResult<PurchasesOfferingsLike>> {
  if (!Capacitor.isNativePlatform()) {
    return {
      success: false,
      error: { code: 'UNKNOWN_ERROR', message: '네이티브 플랫폼에서만 사용 가능합니다.' },
    };
  }
  try {
    const { Purchases } = await loadModule();
    const offerings = await Purchases.getOfferings();
    return {
      success: true,
      data: offerings as unknown as PurchasesOfferingsLike,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'UNKNOWN_ERROR',
        message: error instanceof Error ? error.message : 'Offerings 조회 실패',
      },
    };
  }
}

/** 패키지 구매 (취소 시 success + null 반환) */
export async function purchasePackage(
  pkg: PurchasesPackageLike
): Promise<ServiceResult<SubscriptionInfo | null>> {
  if (!Capacitor.isNativePlatform()) {
    return {
      success: false,
      error: { code: 'UNKNOWN_ERROR', message: '네이티브 플랫폼에서만 사용 가능합니다.' },
    };
  }
  try {
    const { Purchases } = await loadModule();
    const { customerInfo } = await Purchases.purchasePackage({
      // RC SDK accepts the original PurchasesPackage shape; opaque cast is safe
      // because PaywallModal forwards offerings.availablePackages from RC.
      aPackage: pkg as unknown as Parameters<
        typeof Purchases.purchasePackage
      >[0]['aPackage'],
    });
    syncSubscriptionToServer();
    return {
      success: true,
      data: extractSubscriptionInfo(customerInfo as unknown as CustomerInfoLike),
    };
  } catch (error: unknown) {
    const mod = await loadModule();
    const rcError = error as { code?: number; message?: string };
    if (rcError.code === Number(mod.PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR)) {
      return { success: true, data: null };
    }
    return {
      success: false,
      error: {
        code: 'UNKNOWN_ERROR',
        message: rcError.message ?? '구매 처리 실패',
      },
    };
  }
}

/** 구매 복원 */
export async function restorePurchases(): Promise<ServiceResult<SubscriptionInfo>> {
  if (!Capacitor.isNativePlatform()) {
    return {
      success: true,
      data: { isPro: false, plan: 'free', expirationDate: null, productId: null },
    };
  }
  try {
    const { Purchases } = await loadModule();
    const { customerInfo } = await Purchases.restorePurchases();
    syncSubscriptionToServer();
    return {
      success: true,
      data: extractSubscriptionInfo(customerInfo as unknown as CustomerInfoLike),
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'UNKNOWN_ERROR',
        message: error instanceof Error ? error.message : '구매 복원 실패',
      },
    };
  }
}

/** CustomerInfo 업데이트 리스너 등록 */
export async function addCustomerInfoListener(
  callback: (info: SubscriptionInfo) => void
): Promise<(() => void) | null> {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }
  try {
    const { Purchases } = await loadModule();
    const callbackId = await Purchases.addCustomerInfoUpdateListener(
      (customerInfo: unknown) => {
        callback(extractSubscriptionInfo(customerInfo as CustomerInfoLike));
      }
    );
    return () => {
      Purchases.removeCustomerInfoUpdateListener({ listenerToRemove: callbackId });
    };
  } catch {
    return null;
  }
}

/**
 * 서버에 구독 상태 동기화 요청 (fire-and-forget)
 *
 * sync-subscription Edge Function을 호출하여 RC API로 최신 구독 상태를 DB에 반영합니다.
 * 실패해도 구매 흐름에 영향을 주지 않습니다.
 */
async function syncSubscriptionToServer(): Promise<void> {
  try {
    const { supabase } = await import('./supabase');
    const { error } = await supabase.functions.invoke('sync-subscription', {
      method: 'POST',
    });
    if (error) {
      console.warn('[subscription] sync-subscription failed:', error.message);
    }
  } catch (e) {
    console.warn('[subscription] sync-subscription error:', e);
  }
}

/** 구독 관리 페이지 열기 (App Store/Play Store) */
export async function showManageSubscriptions(): Promise<ServiceResult<void>> {
  if (!Capacitor.isNativePlatform()) {
    return {
      success: false,
      error: { code: 'UNKNOWN_ERROR', message: '네이티브 플랫폼에서만 사용 가능합니다.' },
    };
  }
  try {
    const { Purchases } = await loadModule();
    const { customerInfo } = await Purchases.getCustomerInfo();
    const managementURL = (customerInfo as { managementURL?: string | null })
      .managementURL;
    if (managementURL) {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: managementURL });
      return { success: true, data: undefined };
    }
    return {
      success: false,
      error: { code: 'NOT_FOUND', message: '구독 관리 URL을 찾을 수 없습니다.' },
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'UNKNOWN_ERROR',
        message: error instanceof Error ? error.message : '구독 관리 페이지 열기 실패',
      },
    };
  }
}
